import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLenis } from "lenis/react";
import { List, X, EnvelopeSimple, InstagramLogo, WhatsappLogo } from "@phosphor-icons/react";
import { useContent } from "@/context/ContentContext";
import { useIntro } from "@/context/IntroContext";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "About", id: "studio" },
  { label: "Projects", id: "work" },
  { label: "Journal", id: "journal" },
  { label: "Contact", id: "contact" },
];

export default function Nav({ brand = "NAT HABITAT" }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onHome = location.pathname === "/";
  const overHero = onHome && !scrolled; // transparent, seamless header only at the very top of the home hero
  const { introActive } = useIntro();
  const content = useContent();
  const social = { email: "natasha@nathabitat.com", instagram: "https://www.instagram.com/natasharaisi/", whatsapp: "https://wa.me/971507770224", ...(content?.social || {}) };

  const go = (id) => {
    setOpen(false);
    if (id === "home") {
      if (location.pathname !== "/") { navigate("/"); return; }
      if (lenis) lenis.scrollTo(0); else window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (id === "journal") {
      navigate("/journal");
      if (lenis) lenis.scrollTo(0); else window.scrollTo({ top: 0 });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 400);
      return;
    }
    const el = document.getElementById(id);
    if (el && lenis) lenis.scrollTo(el, { offset: -20 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const iconCls = "transition-colors duration-300 text-white/85 hover:text-white";
  const circleCls = "h-9 w-9 rounded-full border border-white/40 text-white/90 grid place-items-center transition-colors duration-300 hover:bg-white hover:text-[#5f5648]";

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-500 ${
        overHero ? "py-5" : "py-3 border-b"
      }`}
      style={{
        backgroundColor: overHero ? "transparent" : "rgba(138,125,109,0.94)",
        borderColor: overHero ? "transparent" : "rgba(255,255,255,0.14)",
        backdropFilter: overHero ? "none" : "blur(10px)",
        WebkitBackdropFilter: overHero ? "none" : "blur(10px)",
      }}
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 md:gap-4 text-white transition-colors duration-300" style={{ opacity: introActive ? 0 : 1, transition: "opacity 0.5s ease" }}>
          <img
            src="/logo-nh.png"
            alt="Nat Habitat monogram"
            data-testid="nav-logo-mark"
            className="h-12 md:h-16 w-auto"
          />
          <span className="w-px h-9 md:h-11 bg-current opacity-40" />
          <span className="flex flex-col leading-none">
            <span className="font-serif text-lg md:text-2xl tracking-[0.32em] font-light">NAT HABITAT</span>
            <span className="text-[7px] md:text-[9px] tracking-[0.3em] mt-1.5 opacity-80">INTERIOR DESIGN STUDIO</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="font-serif text-lg tracking-wide transition-colors duration-300 text-white/90 hover:text-white"
            >
              {l.label}
            </button>
          ))}
          <div className="flex items-center gap-3 pl-3">
            {social.email && (
              <a href={`mailto:${social.email}`} data-testid="nav-social-email" aria-label="Email" className={circleCls}>
                <EnvelopeSimple size={16} />
              </a>
            )}
            {social.instagram && (
              <a href={social.instagram} target="_blank" rel="noopener noreferrer" data-testid="nav-social-instagram" aria-label="Instagram" className={circleCls}>
                <InstagramLogo size={16} />
              </a>
            )}
            {social.whatsapp && (
              <a href={social.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="nav-social-whatsapp" aria-label="WhatsApp" className={circleCls}>
                <WhatsappLogo size={16} />
              </a>
            )}
          </div>
        </nav>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-toggle" aria-label="Menu">
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden mt-4 px-6 py-6 flex flex-col gap-5" data-testid="mobile-menu" style={{ backgroundColor: "rgba(138,125,109,0.98)" }}>
          {LINKS.map((l) => (
            <button key={l.id} onClick={() => go(l.id)} className="text-left text-sm uppercase tracking-[0.2em] text-white/90">
              {l.label}
            </button>
          ))}
          <div className="flex items-center gap-6 pt-4 border-t border-white/20">
            {social.email && <a href={`mailto:${social.email}`} className="text-white/85" aria-label="Email"><EnvelopeSimple size={22} /></a>}
            {social.instagram && <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="text-white/85" aria-label="Instagram"><InstagramLogo size={22} /></a>}
            {social.whatsapp && <a href={social.whatsapp} target="_blank" rel="noopener noreferrer" className="text-white/85" aria-label="WhatsApp"><WhatsappLogo size={22} /></a>}
          </div>
        </div>
      )}
    </header>
  );
}
